import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, Paper } from "@mui/material";
import { motion } from "framer-motion";
import AboutImg from "../assets/About.png";
import About2 from "../assets/About2.jpg";
import AboutUsMobile from "./aboutUsMobile";
import { getAboutUs } from "../services/api/getAll";
import { useTranslation } from "react-i18next";
import bgImage from "../assets/kiki2.jpeg";

const AboutUs = () => {
  const ref = useRef(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [aboutContent, setAboutContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const renderHighlightedText = (text: string) => {
    if (!text) return "";
    const parts = text.split(/(TowHelp)/gi);
    return parts.map((part, index) =>
      part.toLowerCase() === "towhelp" ? (
        <span
          key={index}
          style={{
            color: "#FFAE00",
            fontWeight: 800,
            display: "inline-block",
            direction: "ltr",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAboutUs();
        const preferredLanguage = localStorage.getItem("language") || "en";
        const item = response.items?.[response?.items?.length - 1];
        const translation = item?.translations.find(
          (t: { languageCode: string }) => t.languageCode === preferredLanguage
        );
        setAboutContent(translation?.content || "");
      } catch (err: any) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isMobile) {
    return <AboutUsMobile />;
  }

  return (
    <Box
      ref={ref}
      id="about"
      sx={{
        position: "relative",
        py: { xs: 10, md: 16 },
        px: 3,
        color: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "transparent",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 2, width: "100%", maxWidth: "1220px" }}>
        <motion.div
          initial={{ opacity: 0, y: -25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: "center", marginBottom: "64px" }}
        >
          <Typography
            component="span"
            variant="h3"
            fontWeight={900}
            textAlign="center"
            sx={{
              fontFamily: "Poppins, Ozen",
              background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textTransform: "uppercase",
              textShadow: "0 0 30px rgba(255, 174, 0, 0.1)",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            {t("aboutTowHelp")}
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: isRTL ? "row-reverse" : "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 6,
            px: 2,
          }}
        >
          {/* Text Container */}
          <motion.div
            key={`${aboutContent ? 1 : 0}-${i18n.language}`}
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] as const }}
            style={{ flex: 1.2 }}
          >
            <Paper
              elevation={0}
              className="premium-service-card"
              sx={{
                p: { xs: 4, md: 6 },
              }}
            >
              <Typography
                variant="body1"
                dir={isRTL ? "rtl" : "ltr"}
                sx={{
                  fontFamily: "Poppins",
                  color: "#e0e0e0",
                  fontSize: { xs: "1.05rem", md: "1.2rem" },
                  lineHeight: 1.8,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {renderHighlightedText(aboutContent)}
              </Typography>
            </Paper>
          </motion.div>

          {/* iPhone Mockups Container */}
          <Box
            sx={{
              flex: 0.8,
              height: { xs: "400px", md: "520px" },
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Left Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: -60, y: 50, rotate: -15 }}
              whileInView={{ opacity: 1, x: 0, y: 0, rotate: -5 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 1.1, delay: 0.1, ease: [0.25, 1, 0.5, 1] as const }}
              style={{
                position: "absolute",
                top: 30,
                left: "calc(50% - 210px)",
                transformOrigin: "bottom left",
                zIndex: 1,
              }}
            >
              <motion.div
                animate={{
                  y: [0, -12, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "200px", md: "230px" },
                    height: { xs: "400px", md: "460px" },
                    borderRadius: "36px",
                    border: "10px solid #1a1a1a",
                    backgroundColor: "#000",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.6), inset 0 0 10px rgba(255,255,255,0.2)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "0",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "100px",
                      height: "18px",
                      backgroundColor: "#1a1a1a",
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                      zIndex: 10,
                    }
                  }}
                >
                  <img
                    src={AboutImg}
                    alt="TowHelp 1"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
              </motion.div>
            </motion.div>

            {/* Right Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 60, y: 50, rotate: 15 }}
              whileInView={{ opacity: 1, x: 0, y: 0, rotate: 5 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 1.1, delay: 0.3, ease: [0.25, 1, 0.5, 1] as const }}
              style={{
                position: "absolute",
                top: 0,
                left: "calc(50% - 80px)",
                zIndex: 2,
                transformOrigin: "bottom right",
              }}
            >
              <motion.div
                animate={{
                  y: [0, -12, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                  delay: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: { xs: "220px", md: "250px" },
                    height: { xs: "440px", md: "500px" },
                    borderRadius: "36px",
                    border: "10px solid #1a1a1a",
                    backgroundColor: "#000",
                    boxShadow: "0 25px 50px rgba(0,0,0,0.7), inset 0 0 10px rgba(255,255,255,0.2)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "0",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "110px",
                      height: "20px",
                      backgroundColor: "#1a1a1a",
                      borderBottomLeftRadius: "14px",
                      borderBottomRightRadius: "14px",
                      zIndex: 10,
                    }
                  }}
                >
                  <img
                    src={About2}
                    alt="TowHelp 2"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
              </motion.div>
            </motion.div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutUs;

